import React from 'react';
import { Calculator, Trash2, Copy } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from './ui/Card';
import { Input } from './ui/Input';
import { Label } from './ui/Label';
import { Select } from './ui/Select';
import { Button } from './ui/Button';
import { GuardrailsIndicator } from './GuardrailsIndicator';
import { LoanScenario, calculateMortgage, formatCurrency } from '../utils/mortgageCalculations';
interface ScenarioCardProps {
  scenario: LoanScenario;
  clientIncome: number;
  clientDebts: number;
  onChange: (id: string, field: keyof LoanScenario, value: any) => void;
  onRemove: (id: string) => void;
  onDuplicate: (scenario: LoanScenario) => void;
  isRemovable: boolean;
}
export function ScenarioCard({
  scenario,
  clientIncome,
  clientDebts,
  onChange,
  onRemove,
  onDuplicate,
  isRemovable
}: ScenarioCardProps) {
  const results = calculateMortgage(scenario, clientIncome, clientDebts);
  const handleChange = (field: keyof LoanScenario, value: string | number) => {
    onChange(scenario.id, field, value);
  };
  return <Card className={`flex flex-col h-full border-t-4 ${results.isCompliant ? 'border-t-blue-500' : 'border-t-red-500'} shadow-md hover:shadow-lg transition-shadow`} data-id="element-49">
      <CardHeader className="pb-4 space-y-4" data-id="element-50">
        <div className="flex items-center justify-between" data-id="element-51">
          <Input value={scenario.name} onChange={e => handleChange('name', e.target.value)} className="font-bold text-lg border-transparent hover:border-slate-200 focus:border-blue-500 px-2 -ml-2 h-9" data-id="element-52" />
          <div className="flex gap-1" data-id="element-53">
            <Button variant="ghost" size="icon" onClick={() => onDuplicate(scenario)} title="Duplicate Scenario" data-id="element-54">
              <Copy className="h-4 w-4 text-slate-500" data-id="element-55" />
            </Button>
            {isRemovable && <Button variant="ghost" size="icon" onClick={() => onRemove(scenario.id)} title="Remove Scenario" data-id="element-56">
                <Trash2 className="h-4 w-4 text-red-400" data-id="element-57" />
              </Button>}
          </div>
        </div>

        <div className="bg-slate-50 p-4 rounded-lg text-center" data-id="element-58">
          <span className="text-sm text-slate-500 font-medium uppercase tracking-wide" data-id="element-59">
            Monthly Payment
          </span>
          <div className="text-3xl font-bold text-slate-900 mt-1" data-id="element-60">
            {formatCurrency(results.monthlyPayment)}
          </div>
          <div className="text-xs text-slate-500 mt-1" data-id="element-61">
            Principal & Interest only
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4 flex-1" data-id="element-62">
        <div className="space-y-3" data-id="element-63">
          <div className="grid grid-cols-2 gap-3" data-id="element-64">
            <div className="space-y-1" data-id="element-65">
              <Label htmlFor={`amount-${scenario.id}`} data-id="element-66">Loan Amount</Label>
              <div className="relative" data-id="element-67">
                <span className="absolute left-3 top-2.5 text-slate-500" data-id="element-68">
                  $
                </span>
                <Input id={`amount-${scenario.id}`} type="number" className="pl-7" value={scenario.loanAmount} onChange={e => handleChange('loanAmount', Number(e.target.value))} data-id="element-69" />
              </div>
            </div>
            <div className="space-y-1" data-id="element-70">
              <Label htmlFor={`value-${scenario.id}`} data-id="element-71">Property Value</Label>
              <div className="relative" data-id="element-72">
                <span className="absolute left-3 top-2.5 text-slate-500" data-id="element-73">
                  $
                </span>
                <Input id={`value-${scenario.id}`} type="number" className="pl-7" value={scenario.propertyValue} onChange={e => handleChange('propertyValue', Number(e.target.value))} data-id="element-74" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-75">
            <div className="space-y-1" data-id="element-76">
              <Label htmlFor={`rate-${scenario.id}`} data-id="element-77">Interest Rate</Label>
              <div className="relative" data-id="element-78">
                <Input id={`rate-${scenario.id}`} type="number" step="0.125" className="pr-6" value={scenario.interestRate} onChange={e => handleChange('interestRate', Number(e.target.value))} data-id="element-79" />
                <span className="absolute right-3 top-2.5 text-slate-500" data-id="element-80">
                  %
                </span>
              </div>
            </div>
            <div className="space-y-1" data-id="element-81">
              <Label htmlFor={`term-${scenario.id}`} data-id="element-82">Term (Years)</Label>
              <Select id={`term-${scenario.id}`} value={scenario.termYears} onChange={e => handleChange('termYears', Number(e.target.value))} data-id="element-83">
                <option value={15} data-id="element-84">15 Years</option>
                <option value={20} data-id="element-85">20 Years</option>
                <option value={30} data-id="element-86">30 Years</option>
              </Select>
            </div>
          </div>

          <div className="space-y-1" data-id="element-87">
            <Label htmlFor={`type-${scenario.id}`} data-id="element-88">Product Type</Label>
            <Select id={`type-${scenario.id}`} value={scenario.productType} onChange={e => handleChange('productType', e.target.value)} data-id="element-89">
              <option value="Fixed" data-id="element-90">Fixed Rate Conventional</option>
              <option value="Variable" data-id="element-91">Variable Rate</option>
              <option value="ARM" data-id="element-92">5/1 ARM</option>
              <option value="FHA" data-id="element-93">FHA Fixed</option>
              <option value="VA" data-id="element-94">VA Fixed</option>
            </Select>
          </div>
        </div>

        <div className="pt-4 border-t border-slate-100 space-y-2" data-id="element-95">
          <h4 className="text-xs font-semibold text-slate-900 uppercase tracking-wider mb-2" data-id="element-96">
            Compliance Guardrails
          </h4>
          <GuardrailsIndicator label="Debt-to-Income (DTI)" value={results.dti} limit={43} type="percent" data-id="element-97" />
          <GuardrailsIndicator label="Loan-to-Value (LTV)" value={results.ltv} limit={95} type="percent" data-id="element-98" />
        </div>
      </CardContent>

      <CardFooter className="bg-slate-50 border-t border-slate-100 p-4" data-id="element-99">
        <Button className="w-full" variant={results.isCompliant ? 'default' : 'secondary'} data-id="element-100">
          {results.isCompliant ? 'Select This Scenario' : 'Review Violations'}
        </Button>
      </CardFooter>
    </Card>;
}